import React, { useState, useCallback, useEffect } from 'react';
import { withTheme } from '@buoyhealth/common.buoy-theme';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import { ErrorText } from '@buoyhealth/common.buoy-input';
import { InterviewAgeLimit } from 'src/machines/shared';
import {
  IPolarisInterviewQuestion,
  MiniInterviewType,
} from 'src/packages/polaris/types/interviews';
import SubmitButton from './SubmitButton';
import useTransition from '../hooks/useTransition';
import { StyledToggleButtonGroup } from './Options';
import useIdentifiers from '../hooks/useIdentifiers';
import useConflictError from '../hooks/useConflictError';
import useOptional from '../hooks/useOptional';
import useIsPolaris from '../hooks/useIsPolaris';
import useParentTransition from '../hooks/useParentTransition';
import useInterviewType from '../hooks/useInterviewType';
import Tooltip from './Tooltip';

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  align-items: center;

  .MuiInputBase-input {
    font-size: 16px;
  }
`;

const StyledSpan = styled.span`
  display: inline-block;
  margin-left: 12px;
  ${({ theme }) => theme.typography.variants.primary.main};
`;

const sexOptions = [
  { value: 'f', label: 'Female', media: '', mediaAltText: '' },
  { value: 'm', label: 'Male', media: '', mediaAltText: '' },
];

interface IConflictMessage {
  sex?: string;
  age?: string;
}

const getAgeTooltipText = (interviewType: MiniInterviewType) => {
  switch (interviewType) {
    case 'treatmentGuidance':
    case 'txoc':
      return 'Different products might be more or less relevant based on your age. Knowing your age allows Buoy to provide more targeted product selections.';
    default:
      return (
        <>
          Buoy Assistant is designed to consult on symptoms for patients between the ages of{' '}
          {InterviewAgeLimit.Min} and {InterviewAgeLimit.Max}.
          <br />
          <br />
          Many illnesses only affect certain age groups. Knowing your age allows Buoy to give a more
          accurate assessment.
        </>
      );
  }
};

const ProfileInput = ({
  question,
  onQuestionSubmit,
}: {
  question?: IPolarisInterviewQuestion;
  onQuestionSubmit?: (questionIndex: number) => void;
}) => {
  const transition = useTransition();
  const conflictError = useConflictError();
  const identifiers = useIdentifiers();
  const isOptional = useOptional();
  const isPolaris = useIsPolaris();
  const parentTransition = useParentTransition();
  const interviewType = useInterviewType();

  const [age, setAge] = useState(identifiers.age.value);
  const [sex, setSex] = useState(identifiers.sex);
  const [formError, setFormError] = useState({
    age: false,
    sex: false,
  });

  const conflictMessages: IConflictMessage | undefined = conflictError?.data?.body;

  useEffect(() => {
    if (conflictMessages) {
      setFormError({
        age: !!conflictMessages?.age,
        sex: !!conflictMessages?.sex,
      });
    }
  }, [conflictMessages]);

  const handleAgeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAge(e.target.value as string);
      setFormError({ age: false, sex: formError.sex });
    },
    [formError.sex],
  );

  const handleSexChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, value: 'm' | 'f') => {
      setSex(value);
      setFormError({ age: formError.age, sex: false });
    },
    [formError.age],
  );

  const handleSubmit = useCallback(() => {
    const ageAndSexRequired = !isOptional;
    if (ageAndSexRequired) {
      if (!age || !sex) {
        return setFormError({ age: !age, sex: !sex });
      }
    }

    const parsedAge = parseInt(age, 10);
    if (parsedAge < InterviewAgeLimit.Min || parsedAge > InterviewAgeLimit.Max) {
      return setFormError({
        age: true,
        sex: ageAndSexRequired ? !sex : false,
      });
    }

    if (question?.index !== undefined) {
      onQuestionSubmit?.(question.index);
    }

    parentTransition({
      event: 'IDENTIFIERS_SUBMITTED',
      data: {
        identifiers: {
          age: {
            unit: 'year',
            value: age,
          },
          sex,
          pov: 'self',
        },
      },
    });

    transition({
      data: {
        profile: {
          age,
          sex,
        },
        question,
        isPolaris,
      },
    });
  }, [age, isOptional, isPolaris, onQuestionSubmit, parentTransition, question, sex, transition]);

  return (
    <>
      <div className="bp-font-primary-small bp-mb-3 bp-flex bp-items-center">
        <span className="bp-mr-3 bp-inline-block">How old are you?</span>
        <Tooltip title={getAgeTooltipText(interviewType)} />
      </div>
      <Row>
        <TextField
          InputProps={{
            sx: { backgroundColor: 'white' },
          }}
          error={formError.age}
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            type: 'number',
            min: '0',
          }}
          label="Age"
          onChange={handleAgeChange}
          type="number"
          value={age}
        />
        <StyledSpan>years old</StyledSpan>
      </Row>
      <ErrorText
        show={formError.age}
        message={
          formError.age
            ? conflictMessages?.age ||
              `This quiz is designed for individuals between the ages of ${InterviewAgeLimit.Min} and ${InterviewAgeLimit.Max}.`
            : ''
        }
      />
      <br />
      <div className="bp-font-primary-small bp-mb-3 bp-flex bp-items-center">
        <span className="bp-mr-3 bp-inline-block">What is your assigned sex at birth?</span>
        <Tooltip title="We recognize that not everyone identifies as female or male, so we're working to incorporate more gender identities into Buoy's algorithm. For now, enter your sex at birth to continue." />
      </div>
      <StyledToggleButtonGroup
        errorMessage={conflictMessages?.sex || ''}
        fullWidth
        hasError={formError.sex}
        onChange={handleSexChange}
        options={sexOptions}
        value={sex}
      />
      <SubmitButton
        errorMessage={formError.sex || formError.age ? 'Please correct the errors above.' : ''}
        isError={formError.sex || formError.age}
        onClick={handleSubmit}
        text={isOptional ? (sex || age ? 'Next' : 'Skip') : undefined}
      />
    </>
  );
};

export default withTheme(ProfileInput);
